/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Audience snapshots", description = "Audience snapshots give data marketers a record of membership history over time.", lastUpdatedDate = new Date(1724716800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Audience snapshots",
        description: "Audience snapshots give data marketers a record of membership history over time.",
        lastUpdatedDate: "Tue Aug 27 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 1,
          "id": "overview"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h3: "h3",
      p: "p",
      a: "a",
      code: "code",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      img: "img"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h3, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Customer Studio is available as an add-on on ", _jsx(_components.a, {
            href: "https://hightouch.com/pricing",
            children: "Business tier\nplans"
          }), "."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Audience snapshots provide a record of membership history over time.\nAfter each sync of an audience, snapshotting sends the IDs of audience members to a table in your data warehouse.\nThe snapshots don't contain any customer data beyond audience members' IDs."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Audience snapshotting requires ", _jsx(_components.code, {
            children: "WRITE"
          }), " permissions to your data warehouse.\nBecause of this, Hightouch only takes snapshots of audiences built off sources\nthat have the ", _jsx(_components.a, {
            href: "/syncs/lightning-sync-engine",
            children: "Lightning sync engine"
          }), " enabled."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Each table row contains the audience member's ID, the timestamp of when the snapshot occurred, and either an ", _jsx(_components.code, {
          children: "enter"
        }), " or ", _jsx(_components.code, {
          children: "exit"
        }), " event type.\nAn audience's first snapshot records current membership in the form of ", _jsx(_components.code, {
          children: "enter"
        }), " events for every member.\nSubsequent snapshots only contain changes to membership—either ", _jsx(_components.code, {
          children: "enter"
        }), " events for new members or ", _jsx(_components.code, {
          children: "exit"
        }), " events for members who aren't present in that snapshot but were in the previous one."]
      }), "\n", _jsxs(_components.p, {
        children: ["Snapshots for all audience members in all audiences are stored in a single table called ", _jsx(_components.code, {
          children: "hightouch_planner.audience_membership"
        }), " with the following schema:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Column"
            }), _jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht_row_id"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The primary key of the user. This is stringified for consistency if the underlying type isn't a string."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht_audience_id"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsx(_components.td, {
              children: "The ID of the audience."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht_timestamp"
            }), _jsx(_components.td, {
              children: "timestamp"
            }), _jsx(_components.td, {
              children: "The time at which the user entered or exited the audience."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht_event_type"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "enter"
              }), " or ", _jsx(_components.code, {
                children: "exit"
              }), " depending on the type of event recorded."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "ht_split_group"
            }), _jsx(_components.td, {
              children: "string"
            }), _jsxs(_components.td, {
              children: ["The split ID that this user is a member of. If the audience doesn't have splits, the column value is ", _jsx(_components.code, {
                children: "null"
              }), "."]
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["To enable audience snapshots, navigate to the ", _jsx(_components.strong, {
          children: "Sync log"
        }), " tab on your source's details page."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "hightouch-audiences/performance/enable-snapshot.png",
          alt: "Enable snapshotting"
        })
      })]
    });
  }
}
export default MDXContent;
